<template>
	<StandardModal
		open
		:title="$t('admin_imports_import_details_title', [batch.id])"
		:confirmText="$t('common_button_close')"
		@confirm="$emit('cancel')"
		@cancel="$emit('cancel')"
		ref="importDetails"
		class="import-details-modal"
	>
		<div class="import-details-wrapper">
			<div class="details">
				<div class="heading">
					{{ $t('admin_imports_import_details_import_status') }}
				</div>
				<div class="import-status">
					<component
						:is="getBatchIcon"
						:class="getBatchIconClass"
						type="regular"
						class="status-icon"
					/>
					<div>{{ $t(batchStatus) }}</div>
					<div v-if="showProgress" class="progress">
						<div class="percentage">{{ `${batch.progress}%` }}</div>
						<div class="progress-bar">
							<div
								class="progress-bar-fill"
								:style="{ width: batch?.progress + '%' }"
							></div>
						</div>
					</div>
				</div>
				<div class="import-details">
					<table>
						<thead>
							<tr>
								<th class="heading">
									{{ $t('admin_imports_table_heading_importedby') }}
								</th>
								<th class="heading">
									{{ $t('admin_imports_table_heading_importedfrom') }}
								</th>
								<th class="heading">
									{{
										!isPending ? $t('admin_imports_import_details_started') : ''
									}}
								</th>
								<th class="heading">
									{{
										isCompleted
											? $t('admin_imports_import_details_finished')
											: ''
									}}
								</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>{{ batch?.user?.fullname }}</td>
								<td>
									{{
										batch?.source === 'api'
											? $t('admin_imports_imported_from_api')
											: $t('admin_imports_imported_from_org')
									}}
								</td>
								<td>
									{{ !isPending ? $m(batch?.created_at).format('lll') : '' }}
								</td>
								<td>
									{{ isCompleted ? $m(batch?.updated_at).format('lll') : '' }}
									<div
										v-if="canCancel"
										class="cancel-link"
										@click="cancelImport"
									>
										{{ $t('admin_imports_import_details_cancel') }}
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>

			<div class="file-details">
				<FileDetail v-for="file in batch?.files" :file="file" :key="file.id" />
			</div>
		</div>
	</StandardModal>
</template>

<script>
import { StandardModal } from '@/components/common'
import FileDetail from './FileDetail'
import { get, put } from '../common'
import {
	TimerIcon,
	ArrowUpFromArcIcon,
	CircleCheckIcon,
	CircleExclamationIcon,
	CircleVIcon,
	CircleXIcon,
} from '@/icons'

export default {
	components: {
		StandardModal,
		FileDetail,
		TimerIcon,
		ArrowUpFromArcIcon,
		CircleCheckIcon,
		CircleExclamationIcon,
		CircleVIcon,
		CircleXIcon,
	},

	props: ['batchdata'],

	data() {
		return {
			batch: {},
		}
	},

	mounted() {
		this.batch = this.batchdata
		this.fetchBatchDetails()
	},

	computed: {
		showProgress() {
			return (
				this.batch?.status === 'importing' ||
				this.batch?.status === 'validating'
			)
		},

		isPending() {
			return this.batch?.status === 'pending'
		},

		isCompleted() {
			return (
				this.batch?.status === 'completed' ||
				this.batch?.status === 'completed_with_errors'
			)
		},

		canCancel() {
			return (
				this.batch?.status === 'importing' ||
				this.batch?.status === 'pending' ||
				this.batch?.status === 'validating'
			)
		},

		batchStatus() {
			if (this.batch?.status) {
				return `admin_imports_batch_status_${this.batch.status}`
			} else {
				return ''
			}
		},

		getBatchIcon() {
			switch (this.batch?.status) {
				case 'pending':
					return 'TimerIcon'
				case 'validating':
					return 'CircleVIcon'
				case 'importing':
					return 'ArrowUpFromArcIcon'
				case 'completed':
					return 'CircleCheckIcon'
				case 'completed_with_errors':
				case 'failed':
					return 'CircleExclamationIcon'
				case 'canceling':
				case 'canceled':
					return 'CircleXIcon'
				default:
					return null
			}
		},

		getBatchIconClass() {
			switch (this.batch?.status) {
				case 'pending':
					return 'pending-icon'
				case 'validating':
					return 'validating-icon'
				case 'importing':
					return 'importing-icon'
				case 'completed':
					return 'completed-icon'
				case 'completed_with_errors':
					return 'completed-errors-icon'
				case 'failed':
					return 'failed-icon'
				case 'canceling':
					return 'canceling-icon'
				case 'canceled':
					return 'cancelled-icon'
				default:
					return null
			}
		},
	},

	methods: {
		async fetchBatchDetails() {
			// this.fetchBatchError = false
			// this.fetchingBatchDetails = true
			try {
				const apiResponse = await get(
					`/organization/import_batches/${this.batch.id}`
				)
				this.batch = apiResponse.data
				// this.fetchingBatchDetails = false
			} catch (e) {
				// this.fetchingBatchDetails = false
				this.$alert.error(this.$t('common_api_error_save'))
				// this.fetchBatchError = true
			}
		},

		async cancelImport() {
			try {
				const apiResponse = await put(
					`/organization/import_batches/${this.batch.id}/cancel`
				)
				this.batch = apiResponse
			} catch (e) {
				this.$alert.error(this.$t('common_api_error_save'))
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.import-details-wrapper {
	padding: 16px 43px;

	.details {
		padding: 16px;
		margin-bottom: 16px;
		background-color: #f9f9f9;
		border: solid 1px var(--neutral-200);
		border-radius: 8px;

		.heading {
			color: var(--neutral-500);
			font-size: 14px;
		}

		.import-status {
			display: flex;
			margin-top: 8px;
		}

		.status-icon {
			font-size: 20px;
			margin-right: 14px;
		}

		.failed-icon,
		.cancelled-icon {
			color: var(--red-400);
		}

		.canceling-icon,
		.completed-errors-icon {
			color: var(--yellow-300);
		}

		.importing-icon,
		.validating-icon {
			color: var(--blue-300);
		}

		.pending-icon {
			color: var(--neutral-400);
		}

		.completed-icon {
			color: var(--teal-300);
		}

		.progress {
			display: flex;
			align-items: center;
			width: 100%;
			margin-left: 8px;
			border-left: solid 1px var(--neutral-200);
			padding-left: 8px;
		}
		.progress-bar {
			height: 6px;
			width: 100%;
			background-color: var(--neutral-200);
			border-radius: 20px;
			margin-left: 8px;
		}
		.progress-bar-fill {
			height: 100%;
			background-color: var(--blue-300);
			width: 50%;
			border-radius: 10px;
		}

		.percentage {
			font-size: 14px;
			color: var(--neutral-600);
		}

		.import-details {
			margin-top: 25px;
			table {
				width: 100%;
			}
			th {
				font-weight: unset;
				text-align: left;
				width: 27%;
				padding-bottom: 11px;
			}

			td {
				width: 27%;
			}

			.cancel-link {
				float: right;
				color: var(--red-400);
				cursor: pointer;
				&:hover {
					color: var(--red-100) !important;
				}
			}
		}
	}
}
</style>
<style lang="scss">
.import-details-modal {
	.modal-content {
		width: 1015px !important;
		height: 800px !important;
	}
}
</style>
